import type { Credentials } from "react-native-auth0";

import { Auth0Client, WebAuth0GetCredentialsArgs } from "@/services/Auth";
import { serializeAuthError } from "@/utils/auth.util";
import { toMs } from "@/utils/date.util";

interface AuthContext {
  isAuthenticated: boolean;
  token: string;
}

const ThrottleRetriesDelayMs = toMs({ seconds: 1 });

const throttleRetries = async () => {
  // since getAuthContext is used in baseQuery's prepareHeaders, it throwing an error will cause
  // the baseQuery to re-try every ~30ms over and over (PER query); so we add this delay
  // to throttle the re-tries and prevent unnecessary CPU usage / battery drain

  // NOTE: this ~30ms retry-on-failure logic is internal to RTKQuery due to the failure being in prepareHeaders (not the request),
  // and is totally unrelated to the usual retry related configuration (# of attempts, timeout, etc)
  await new Promise((resolve) => setTimeout(resolve, ThrottleRetriesDelayMs));
};

/**
 * Get the authentication context for HTTP request headers
 * @returns isAuthenticated and token if authenticated, otherwise throws an error
 */
const getAuthContext = async (): Promise<AuthContext> => {
  // Get authentication state
  const isAuthenticated =
    await Auth0Client.credentialsManager.hasValidCredentials();

  if (!isAuthenticated) {
    await throttleRetries();
    throw new Error(
      "User is not authenticated and can not perform this API query",
    );
  }

  // Get credentials
  let credentials: Credentials;
  try {
    // on web, the args used here must match those used for the authorize() login call
    // on native, this defaults to undefined
    credentials = await Auth0Client.credentialsManager.getCredentials(
      WebAuth0GetCredentialsArgs.scope,
      WebAuth0GetCredentialsArgs.minTtl,
      WebAuth0GetCredentialsArgs.parameters,
    );
  } catch (err) {
    // If Auth0 credentials manager is in a bad state, throttle to avoid
    // RTK Query repeatedly failing the same prepareHeaders call in tight loops.
    await throttleRetries();
    throw serializeAuthError(err);
  }
  const token = credentials.accessToken;

  if (!token) {
    await throttleRetries();
    throw new Error("No access token available");
  }

  return { isAuthenticated, token };
};

export { getAuthContext };
